import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';
import { TrendingUp, RefreshCw, Loader2, Sparkles, KeyRound, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SeoInsights({ onUseTopic }) {
    const { language } = useLanguage();
    const de = language === 'de';
    const [status, setStatus] = useState(null);
    const [insights, setInsights] = useState(null);
    const [keyJson, setKeyJson] = useState('');
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [showQueries, setShowQueries] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/seo/status`).then(res => setStatus(res.data)).catch(() => setStatus({ configured: false }));
        axios.get(`${API}/seo/insights`).then(res => setInsights(res.data)).catch(() => setInsights(null));
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveKey = async () => {
        setSaving(true);
        try {
            const res = await axios.post(`${API}/seo/credentials`, { service_account_json: keyJson });
            setKeyJson('');
            toast.success(de
                ? `Key gespeichert! Füge ${res.data.client_email} in der Search Console als Nutzer hinzu.`
                : `Key saved! Add ${res.data.client_email} as user in Search Console.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || (de ? 'Speichern fehlgeschlagen' : 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const removeKey = async () => {
        await axios.delete(`${API}/seo/credentials`).catch(() => {});
        toast.success(de ? 'Key entfernt' : 'Key removed');
        load();
    };

    const sync = async () => {
        setSyncing(true);
        try {
            const res = await axios.post(`${API}/seo/sync`);
            toast.success(de ? `${res.data.rows} Suchanfragen geladen! 📈` : `Loaded ${res.data.rows} queries! 📈`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || (de ? 'Sync fehlgeschlagen' : 'Sync failed'));
        } finally {
            setSyncing(false);
        }
    };

    if (!status) return null;

    return (
        <Card className="border-2 border-dashed border-emerald-300 bg-emerald-50/40" data-testid="seo-insights">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm font-bold">{de ? 'SEO-Insights (Google)' : 'SEO Insights (Google)'}</span>
                    </div>
                    {status.configured && (
                        <Button size="sm" variant="outline" onClick={sync} disabled={syncing} className="h-8 gap-1.5 text-xs border-emerald-300" data-testid="seo-sync-btn">
                            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            {de ? 'Aktualisieren' : 'Sync'}
                        </Button>
                    )}
                </div>

                {!status.configured ? (
                    <div className="space-y-2" data-testid="seo-setup">
                        <p className="text-xs text-muted-foreground">
                            {de
                                ? 'Verbinde die Google Search Console: Nanouk analysiert dann wöchentlich deine Rankings und schlägt Blog-Themen vor. Füge den Inhalt deiner Service-Account-JSON-Datei ein:'
                                : 'Connect Google Search Console: Nanouk will analyze your rankings weekly and suggest blog topics. Paste your service account JSON file content:'}
                        </p>
                        <Textarea
                            value={keyJson}
                            onChange={(e) => setKeyJson(e.target.value)}
                            placeholder='{"type": "service_account", "project_id": ...}'
                            className="text-xs font-mono h-24 bg-white"
                            data-testid="seo-key-input"
                        />
                        <Button size="sm" onClick={saveKey} disabled={saving || !keyJson.trim()} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="seo-key-save-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                            {de ? 'Key speichern' : 'Save key'}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px]">{status.client_email}</Badge>
                            {status.last_sync && <span>{de ? 'Letzter Sync:' : 'Last sync:'} {new Date(status.last_sync).toLocaleDateString(de ? 'de-DE' : 'en-US')}</span>}
                            <button onClick={removeKey} className="text-red-500 inline-flex items-center gap-0.5" data-testid="seo-key-remove-btn">
                                <Trash2 className="w-3 h-3" /> {de ? 'Key entfernen' : 'Remove key'}
                            </button>
                        </div>

                        {status.last_error && (
                            <p className="text-xs text-red-600 bg-red-50 rounded-lg p-2 border border-red-200" data-testid="seo-error">{status.last_error}</p>
                        )}

                        {!status.last_sync && !status.last_error && (
                            <p className="text-xs text-muted-foreground">
                                {de ? 'Klicke auf "Aktualisieren", um die ersten Daten zu laden. Wichtig: Die Service-Account-E-Mail muss in der Search Console als Nutzer hinzugefügt sein.' : 'Click "Sync" to load first data. Important: the service account email must be added as a user in Search Console.'}
                            </p>
                        )}

                        {insights?.suggestions?.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-xs font-semibold">{de ? '💡 Themen-Vorschläge aus deinen Rankings:' : '💡 Topic suggestions from your rankings:'}</p>
                                {insights.suggestions.slice(0, 5).map((s, i) => {
                                    const reason = (s.reason || '').split('|').find(p => p.startsWith(de ? 'de:' : 'en:'))?.slice(3) || '';
                                    return (
                                        <div key={i} className="rounded-xl border border-emerald-200 bg-white p-2.5 flex items-start justify-between gap-2" data-testid={`seo-suggestion-${i}`}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold truncate">"{s.query}"</p>
                                                <p className="text-[11px] text-muted-foreground">{reason}</p>
                                                <p className="text-[10px] text-emerald-700 mt-0.5">
                                                    {s.impressions} {de ? 'Impressionen' : 'impressions'} · {s.clicks} {de ? 'Klicks' : 'clicks'} · Pos. {s.position}
                                                </p>
                                            </div>
                                            <Button size="sm" onClick={() => onUseTopic?.(s.query)} className="shrink-0 h-7 gap-1 text-[11px] bg-violet-600 hover:bg-violet-700" data-testid={`seo-use-topic-${i}`}>
                                                <Sparkles className="w-3 h-3" /> {de ? 'Artikel' : 'Article'}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {insights?.top_queries?.length > 0 && (
                            <div>
                                <button onClick={() => setShowQueries(!showQueries)} className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid="seo-queries-toggle">
                                    {de ? 'Top-Suchanfragen (28 Tage)' : 'Top queries (28 days)'}
                                    {showQueries ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </button>
                                {showQueries && (
                                    <div className="mt-2 rounded-xl border border-emerald-200 bg-white overflow-hidden" data-testid="seo-queries-table">
                                        <table className="w-full text-[11px]">
                                            <thead className="bg-emerald-50 text-emerald-900">
                                                <tr>
                                                    <th className="text-left p-2 font-semibold">{de ? 'Suchanfrage' : 'Query'}</th>
                                                    <th className="text-right p-2 font-semibold">Impr.</th>
                                                    <th className="text-right p-2 font-semibold">{de ? 'Klicks' : 'Clicks'}</th>
                                                    <th className="text-right p-2 font-semibold">Pos.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {insights.top_queries.map((q, i) => (
                                                    <tr key={i} className="border-t border-emerald-100">
                                                        <td className="p-2 truncate max-w-[160px]">{q.query}</td>
                                                        <td className="p-2 text-right">{q.impressions}</td>
                                                        <td className="p-2 text-right">{q.clicks}</td>
                                                        <td className="p-2 text-right">{q.position}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {status.last_sync && insights?.suggestions?.length === 0 && insights?.top_queries?.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                {de ? 'Noch keine Daten von Google — bei neuen Properties dauert es einige Tage, bis Suchdaten auflaufen.' : 'No data from Google yet — new properties need a few days before search data appears.'}
                            </p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
